import axios from 'axios';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// S3 Service class using backend API (C# Style Compatible)
class S3Service {
  constructor() {
    this.uploadApiUrl = `${API_BASE_URL}/upload`; // New upload endpoints
    this.filesApiUrl = `${API_BASE_URL}/files`;   // Legacy file management endpoints
  }

  /**
   * Upload a file via backend API
   * @param {File} file - The file to upload
   * @param {string} folder - The folder path in S3 (e.g., 'applications/67474/')
   * @param {string} fileName - Custom file name (optional)
   * @param {function} onProgress - Progress callback function
   * @returns {Promise} - Returns upload result with file URL
   */
  async uploadFile(file, folder = '', fileName = null, onProgress = null) {
    try {
      if (!file) {
        throw new Error('No file provided for upload');
      }

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fieldName', fileName || file.name);

      // Extract orgId and section from folder path
      const folderParts = folder.split('/').filter(part => part); // Remove empty parts
      if (folderParts.length >= 2 && folderParts[0] === 'applications') {
        formData.append('orgId', folderParts[1]);
        formData.append('section', folderParts.slice(2).join('/') || 'documents');
      } else {
        // If no orgId in folder path, use folder as section
        formData.append('section', folder || 'documents');
      }

      // Upload configuration with progress tracking
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && typeof onProgress === 'function') {
            const percentCompleted = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(percentCompleted);
          }
        }
      };

      // Make API call to new single file upload endpoint
      const response = await axios.post(`${this.uploadApiUrl}/single`, formData, config);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'File uploaded successfully'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Upload failed',
          data: null
        };
      }

    } catch (error) {
      console.error('S3 Upload Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to upload file',
        data: null
      };
    }
  }

  /**
   * Upload multiple files via backend API
   * @param {FileList|Array} files - Array of files to upload
   * @param {string} folder - The folder path in S3
   * @param {function} onProgress - Progress callback function (receives overall progress)
   * @returns {Promise} - Returns array of upload results
   */
  async uploadMultipleFiles(files, folder = '', onProgress = null) {
    try {
      const fileArray = Array.from(files);
      
      // Create FormData
      const formData = new FormData();
      fileArray.forEach(file => {
        formData.append('files', file);
      });
      formData.append('section', folder);

      // Extract orgId from folder if present
      const folderParts = folder.split('/');
      if (folderParts.length > 1) {
        formData.append('orgId', folderParts[1]);
        formData.append('section', folderParts.slice(2).join('/') || 'documents');
      }

      // Upload configuration with progress tracking
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && typeof onProgress === 'function') {
            const percentCompleted = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(percentCompleted);
          }
        }
      };

      // Make API call
      const response = await axios.post(`${this.uploadApiUrl}/multiple`, formData, config);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Files uploaded successfully'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Upload failed',
          data: null
        };
      }

    } catch (error) {
      console.error('Multiple File Upload Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to upload files',
        data: null
      };
    }
  }

  /**
   * Upload file from base64 string (C# Style - matches backend UploadFile method)
   * @param {string} filename - Base filename without extension
   * @param {string} base64Content - Base64 encoded file content (without data: prefix)
   * @param {string} contentType - MIME content type
   * @param {string} ext - File extension (including dot, e.g., '.pdf')
   * @returns {Promise} - Returns upload result with file URL
   */
  async uploadFileFromBase64(filename, base64Content, contentType, ext) {
    try {
      if (!filename || !base64Content || !contentType || !ext) {
        throw new Error('Missing required parameters for base64 upload');
      }

      // Prepare request data matching C# method signature
      const requestData = {
        filename: filename,
        file: base64Content, // Base64 content without data: prefix
        contentType: contentType,
        ext: ext
      };

      // Make API call to base64 upload endpoint
      const response = await axios.post(`${this.uploadApiUrl}/base64`, requestData, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        return {
          success: true,
          data: {
            ...response.data.data,
            fileName: response.data.data.imgName, // Use imgName from C# style response
            originalName: `${filename}${ext}`
          },
          message: response.data.message || 'File uploaded successfully via base64'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Base64 upload failed',
          data: null
        };
      }

    } catch (error) {
      console.error('S3 Base64 Upload Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to upload file from base64',
        data: null
      };
    }
  }

  /**
   * Delete a file via backend API
   * @param {string} key - The S3 key of the file to delete
   * @returns {Promise} - Returns deletion result
   */
  async deleteFile(key) {
    try {
      const encodedKey = encodeURIComponent(key);
      const response = await axios.delete(`${this.uploadApiUrl}/delete/${encodedKey}`);

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'File deleted successfully',
          data: { key }
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Delete failed',
          data: null
        };
      }

    } catch (error) {
      console.error('S3 Delete Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to delete file',
        data: null
      };
    }
  }

  /**
   * Remove file reference only (keeps file in S3) via backend API
   * @param {string} orgId - Organization ID
   * @param {string} fieldName - Field name
   * @returns {Promise} - Returns removal result
   */
  async removeFileReference(orgId, fieldName) {
    try {
      const response = await axios.post(`${this.uploadApiUrl}/remove-reference`, {
        orgId,
        fieldName
      });

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'File reference removed successfully',
          data: response.data.data
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Remove reference failed',
          data: null
        };
      }

    } catch (error) {
      console.error('S3 Remove Reference Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to remove file reference',
        data: null
      };
    }
  }

  /**
   * Generate a presigned URL for file access via backend API
   * @param {string} key - The S3 key of the file
   * @returns {Promise} - Returns presigned URL
   */
  async getPresignedUrl(key) {
    try {
      const encodedKey = encodeURIComponent(key);
      const response = await axios.get(`${this.uploadApiUrl}/url/${encodedKey}`);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Presigned URL generated successfully'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to generate URL',
          data: null
        };
      }

    } catch (error) {
      console.error('Presigned URL Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to generate presigned URL',
        data: null
      };
    }
  }

  /**
   * Get S3 URL for viewing/downloading a file (convenience method)
   * @param {string} fileName - The file name or S3 key
   * @param {string} orgId - Organization ID (optional, will be prepended if not in fileName)
   * @param {string} section - Section/folder name (optional)
   * @returns {Promise<string>} - Returns the presigned URL string or empty string on error
   */
  async getS3Url(fileName, orgId = '', section = '') {
    if (!fileName || fileName.trim() === '') {
      return '';
    }

    try {
      // Build the S3 key based on the structure
      let objectKey = fileName;
      
      // If fileName doesn't already include the full path, build it
      if (orgId && !fileName.includes(`applications/${orgId}/`)) {
        const sectionPart = section ? `${section}/` : '';
        objectKey = `applications/${orgId}/${sectionPart}${fileName}`;
      }

      const result = await this.getPresignedUrl(objectKey);
      
      if (result.success) {
        return result.data.url || result.data.presignedUrl || result.data;
      } else {
        console.error('Failed to get S3 URL:', result.error);
        return '';
      }
    } catch (error) {
      console.error('Error in getS3Url:', error);
      return '';
    }
  }

  /**
   * Check if file exists via backend API
   * @param {string} key - The S3 key of the file
   * @returns {Promise} - Returns file existence result
   */
  async fileExists(key) {
    try {
      const encodedKey = encodeURIComponent(key);
      const response = await axios.get(`${this.uploadApiUrl}/exists/${encodedKey}`);

      if (response.data.success) {
        return {
          success: true,
          exists: response.data.exists,
          data: response.data.data,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to check file existence',
          data: null
        };
      }

    } catch (error) {
      console.error('File Existence Check Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to check file existence',
        data: null
      };
    }
  }

  /**
   * Get file metadata via backend API
   * @param {string} key - The S3 key of the file
   * @returns {Promise} - Returns file metadata
   */
  async getFileMetadata(key) {
    try {
      const encodedKey = encodeURIComponent(key);
      const response = await axios.get(`${this.uploadApiUrl}/metadata/${encodedKey}`);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'File metadata retrieved successfully'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to get file metadata',
          data: null
        };
      }

    } catch (error) {
      console.error('File Metadata Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to get file metadata',
        data: null
      };
    }
  }

  /**
   * List files in a folder via backend API
   * @param {string} folder - The folder path
   * @param {string} orgId - Organization ID
   * @returns {Promise} - Returns list of files
   */
  async listFiles(folder = '', orgId = '') {
    try {
      const response = await axios.get(`${this.uploadApiUrl}/list`, {
        params: { 
          section: folder,
          orgId: orgId,
          maxKeys: 1000 
        }
      });

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Files listed successfully'
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to list files',
          data: null
        };
      }

    } catch (error) {
      console.error('List Files Error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to list files',
        data: null
      };
    }
  }

  /**
   * Get direct S3 URL (no expiry) - Enhanced method
   * @param {string} fileName - The file name or S3 key
   * @param {string} orgId - Organization ID (optional)
   * @param {string} section - Section/folder name (optional)
   * @returns {Promise<string>} - Returns direct S3 URL without expiry
   */
  async getS3UrlDirect(fileName, orgId = '', section = '') {
    if (!fileName || fileName.trim() === '') {
      return '';
    }

    try {
      // Build the S3 key
      let objectKey = fileName;
      
      if (fileName.includes('live/')) {
        objectKey = fileName;
      } else if (fileName.includes('applications/')) {
        objectKey = `live/${fileName}`;
      } else if (orgId && !fileName.includes(`applications/${orgId}/`)) {
        const sectionPart = section ? `${section}/` : 'documents/';
        objectKey = `live/applications/${orgId}/${sectionPart}${fileName}`;
      }

      // Try the backend direct URL endpoint
      const result = await this.getDirectS3Url(objectKey);
      
      if (result.success) {
        return result.data.url || result.data.directUrl;
      }

      // Fallback: Build direct URL manually
      const bucketName = process.env.REACT_APP_AWS_S3_BUCKET || 'nabl7t';
      const region = process.env.REACT_APP_AWS_REGION || 'ap-south-1';
      
      return `https://${bucketName}.s3.${region}.amazonaws.com/${objectKey}`;
      
    } catch (error) {
      console.error('Error in getS3UrlDirect:', error);
      
      // Final fallback: Build URL manually
      const bucketName = process.env.REACT_APP_AWS_S3_BUCKET || 'nabl7t';
      const region = process.env.REACT_APP_AWS_REGION || 'ap-south-1';
      
      let objectKey = fileName;
      if (!fileName.includes('live/') && orgId) {
        const sectionPart = section ? `${section}/` : 'documents/';
        objectKey = `live/applications/${orgId}/${sectionPart}${fileName}`;
      }
      
      return `https://${bucketName}.s3.${region}.amazonaws.com/${objectKey}`;
    }
  }

  /**
   * Simple direct URL builder (like PHP getObjectUrl)
   * @param {string} fileName - The file name
   * @param {string} orgId - Organization ID
   * @param {string} section - Section name
   * @returns {string} - Direct S3 URL
   */
  buildDirectUrl(fileName, orgId = '', section = 'documents') {
    const bucketName = process.env.REACT_APP_AWS_S3_BUCKET || 'nabl7t';
    const region = process.env.REACT_APP_AWS_REGION || 'ap-south-1';
    
    let objectKey = fileName;
    
    if (!fileName.includes('live/')) {
      if (orgId && !fileName.includes(`applications/${orgId}/`)) {
        objectKey = `live/applications/${orgId}/${section}/${fileName}`;
      } else if (fileName.includes('applications/')) {
        objectKey = `live/${fileName}`;
      } else {
        objectKey = `live/${fileName}`;
      }
    }
    
    return `https://${bucketName}.s3.${region}.amazonaws.com/${objectKey}`;
  }
}

// Create and export a singleton instance
const s3Service = new S3Service();
export default s3Service;

// Export the class for custom instances if needed
export { S3Service };
